'use client';

import { useState, useEffect, useCallback } from 'react';
import { useAuctionDetails as useAuctionDetailsQuery } from '../../queries/useAuctionQueries';
import { useProfile } from '../../queries/useProfileQueries';
import type { AuctionDetailsResponse, Bid } from '../../services/generated/Api';

interface UseAuctionDetailsOptions {
  auctionId?: string;
}

interface UseAuctionDetailsResult {
  auction: AuctionDetailsResponse | null;
  loading: boolean;
  error: string;
  isInitialLoad: boolean;
  currentUserId: string | null;
  allBids: Bid[];
  setAllBids: React.Dispatch<React.SetStateAction<Bid[]>>;
  loadAuctionDetails: () => Promise<void>;
}

export const useAuctionDetails = ({ auctionId }: UseAuctionDetailsOptions): UseAuctionDetailsResult => {
  const [allBids, setAllBids] = useState<Bid[]>([]);
  const [isInitialBidsLoaded, setIsInitialBidsLoaded] = useState(false);

  const {
    data: auction,
    isLoading: isLoadingAuction,
    error: auctionError,
    refetch: refetchAuction,
  } = useAuctionDetailsQuery(auctionId, !!auctionId);

  const { data: profile } = useProfile();
  const currentUserId = profile?.id || null;

  // Use stable reference for bids comparison (first bid id + count)
  const bidsKey = auction?.bids ? `${auction.bids[0]?.id}-${auction.bids.length}` : null;

  // Initialize bids from auction details on first load
  useEffect(() => {
    if (auction?.bids && !isInitialBidsLoaded) {
      setAllBids(auction.bids);
      setIsInitialBidsLoaded(true);
    }
  }, [bidsKey, isInitialBidsLoaded]);

  // Merge new bids on subsequent refetches
  useEffect(() => {
    if (auction?.bids && isInitialBidsLoaded) {
      setAllBids((prevBids) => {
        const existingBidIds = new Set(prevBids.map((bid) => bid.id));
        const newBids = auction.bids.filter((bid) => !existingBidIds.has(bid.id));
        if (newBids.length === 0) return prevBids;
        return [...newBids, ...prevBids];
      });
    }
  }, [bidsKey, isInitialBidsLoaded]);

  const error = auctionError
    ? auctionError instanceof Error
      ? auctionError.message
      : 'Failed to load auction details'
    : '';

  const loadAuctionDetails = useCallback(async () => {
    await refetchAuction();
  }, [refetchAuction]);

  return {
    auction: auction || null,
    loading: isLoadingAuction,
    error,
    isInitialLoad: isLoadingAuction && !auction,
    currentUserId,
    allBids,
    setAllBids,
    loadAuctionDetails,
  };
};
